import React, { useState } from 'react';
import { SypacButton, SypacText } from '@sypac/component-library-react';
import { T, useTranslate } from '@tolgee/react';
import {
	TransportConfigService,
	TransportCostPreview,
} from '../../../../services/transport-config.services';

/**
 * Mini-calculator (spec §4.7.4): distance + tons → what the SAVED config
 * charges for one truck. Defaults reproduce the client's sheet example
 * (50 km, 26 t → 527.80 / 554.19 with the seed values).
 */
const CostPreview: React.FC<{ countryCode: 'PL' | 'MD'; currency: string }> = ({
	countryCode,
	currency,
}) => {
	const { t } = useTranslate();
	const [distance, setDistance] = useState<string>('50');
	const [tons, setTons] = useState<string>('26');
	const [result, setResult] = useState<TransportCostPreview | undefined>();
	const [error, setError] = useState<string | undefined>();
	const [loading, setLoading] = useState<boolean>(false);

	const calculate = async () => {
		setLoading(true);
		setError(undefined);
		try {
			const { data } = await TransportConfigService.previewTransportCost({
				countryCode,
				distance: parseFloat(distance),
				tons: parseFloat(tons),
			});
			setResult(data);
		} catch (e: any) {
			const message = e?.response?.data?.message;
			setResult(undefined);
			setError(
				Array.isArray(message)
					? message.join('; ')
					: message ||
							t('transportationFormula.previewFailed', 'Could not calculate'),
			);
		} finally {
			setLoading(false);
		}
	};

	const money = (value?: number) =>
		value === undefined ? '—' : `${value.toFixed(2)} ${currency}`;

	const input = (
		value: string,
		onChange: (v: string) => void,
		label: React.ReactNode,
		suffix: string,
	) => (
		<label className="flex items-center gap-2">
			<SypacText variant="body-regular-medium">
				<p className="text-gray-80">{label}</p>
			</SypacText>
			<input
				type="number"
				min="0"
				step="0.1"
				value={value}
				onChange={(e) => onChange(e.target.value)}
				className="w-[96px] h-[42px] px-3 rounded-[8px] border border-solid border-gray-10 bg-[#FAFAFA]"
			/>
			<SypacText variant="body-regular-medium">
				<p className="text-gray-40">{suffix}</p>
			</SypacText>
		</label>
	);

	const cell = (label: React.ReactNode, value: string) => (
		<div className="flex flex-col">
			<SypacText variant="body-regular-small">
				<p className="text-gray-40">{label}</p>
			</SypacText>
			<SypacText variant="body-regular-medium">
				<p className="text-gray-80">{value}</p>
			</SypacText>
		</div>
	);

	return (
		<section className="ml-5 mr-7.5 xl:mx-7.5 flex flex-col gap-3 p-4 border border-solid border-gray-10 rounded-[10px]">
			<SypacText variant="body-regular-medium">
				<p className="text-gray-40">
					<T keyName="transportationFormula.previewTitle">
						Check one truck with the saved values
					</T>
				</p>
			</SypacText>
			<div className="flex flex-wrap items-center gap-5">
				{input(
					distance,
					setDistance,
					<T keyName="transportationFormula.distance">Distance</T>,
					'km',
				)}
				{input(
					tons,
					setTons,
					<T keyName="transportationFormula.tonsOnTruck">Tons on truck</T>,
					't',
				)}
				<SypacButton variant="secondary" size="small">
					<button
						type="button"
						className="w-[160px] h-[42px] rounded-[8px]"
						disabled={loading || !distance || !tons}
						onClick={calculate}
					>
						<SypacText variant="body-regular-medium">
							<p>
								<T keyName="transportationFormula.calculate">Calculate</T>
							</p>
						</SypacText>
					</button>
				</SypacButton>
			</div>
			{error && (
				<SypacText variant="body-regular-small">
					<p className="text-red">{error}</p>
				</SypacText>
			)}
			{result && (
				<div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-4">
					{cell(
						<T keyName="transportationFormula.zone">Zone</T>,
						`${result.zoneId} · ${
							result.pricingMode === 'DAILY_RATE'
								? t('transportationFormula.modeDailyRate', 'Daily rate')
								: t('transportationFormula.modePerTonKm', 'Per ton·km')
						}`,
					)}
					{cell(
						<T keyName="transportationFormula.category">Category</T>,
						result.categoryId,
					)}
					{cell(
						<T keyName="transportationFormula.coursesPerDay">Courses / day</T>,
						result.coursesPerDay === undefined
							? '—'
							: result.coursesPerDay.toFixed(2),
					)}
					{cell(
						<T keyName="transportationFormula.costPerTon">Cost / t</T>,
						money(result.costPerTon),
					)}
					{cell(
						<T keyName="transportationFormula.costPerTruck">Cost / truck</T>,
						money(result.costPerTruck),
					)}
					{cell(
						<T keyName="transportationFormula.pricePerTon">
							Price / t (with margin)
						</T>,
						money(result.pricePerTon),
					)}
					{cell(
						<T keyName="transportationFormula.pricePerTruck">
							Price / truck (with margin)
						</T>,
						money(result.pricePerTruck),
					)}
				</div>
			)}
		</section>
	);
};

export default CostPreview;
